import { getStockInsidersFlags } from "@/controllers/stock-insiders";
import { classNames } from "@/lib/utils";
import { Stock } from "@/models/stock";
import { StockInsidersFlag } from "@/models/stock-insiders";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import type { ChartEvent } from "@ortex-charts/financial";
import UpgradePlan from "../upgrade-plan/UpgradePlan";
import styles from "./InsiderTradesAndPerformance.module.scss";
import TimeSeriesChart, { type TimeSeries } from "@/components/ortex-charts/TimeSeriesChart";

interface InsiderTradesAndPerformanceProps {
    stock: Stock;
    /** Show the insider trades on the price line. */
    flags?: boolean;
    chartObfuscated?: boolean;
}

/** Each insider's trades as badges on the price, in the colour the platform gives that insider. */
export function insiderEvents(groups: [string, StockInsidersFlag[]][]): ChartEvent[] {
    const out: ChartEvent[] = [];
    groups.forEach(([key, trades]) => {
        trades.forEach((flag, i) => {
            out.push({ id: `${key}-${i}`, time: flag.x, style: "badge", text: flag.title || "•", color: flag.fillColor, group: flag.name, tooltip: [flag.name, flag.text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()] });
        });
    });
    return out;
}

const InsiderTradesAndPerformance = ({ stock, flags, chartObfuscated }: InsiderTradesAndPerformanceProps) => {
    const [chartShortened, setChartShortened] = useState(false);
    const [price, setPrice] = useState<[number, number][]>([]);
    const [groups, setGroups] = useState<[string, StockInsidersFlag[]][]>([]);

    useEffect(() => {
        let alive = true;
        getStockInsidersFlags(stock.stock_id, "2000-01-01", dayjs().format("YYYY-MM-DD")).then((res) => {
            if (!alive) return;
            setChartShortened(res.ts_shortened);
            delete res.obfuscated;
            delete res.ts_shortened;
            setPrice((res[stock.stock_id] as [number, number][]) ?? []);
            setGroups(Object.entries(res).filter((entry) => Number(entry[0]) !== stock.stock_id) as [string, StockInsidersFlag[]][]);
        });
        return () => {
            alive = false;
        };
    }, [stock.stock_id]);

    const series = useMemo<TimeSeries[]>(() => [{ id: "price", title: stock.name, type: "line", data: price, color: "#31ABA6", precision: 2 }], [price, stock.name]);
    const events = useMemo(() => (flags === false ? [] : insiderEvents(groups)), [groups, flags]);

    return (
        <div className={classNames(styles["chart-container"], chartObfuscated && !chartShortened && "obfuscated")}>
            {chartShortened && <UpgradePlan />}
            <TimeSeriesChart series={series} events={events} eventLegend legend={false} initialRange="2y" rangeButtons={[{ label: "6m", days: 183 }, { label: "YTD", days: 0, kind: "ytd" }, { label: "1y", days: 366 }, { label: "2y", days: 731 }, { label: "3y", days: 1096 }, { label: "All", days: 0 }]} exportName={`${stock.ticker}-insiders`} />
        </div>
    );
};

export default InsiderTradesAndPerformance;
